import * as React from 'react';

import { type SileoPosition, sileo } from 'sileo';

/**
 * Senior Toast Implementation:
 * This hook serves as the core utility for triggering elegant, premium
 * notifications throughout the bioeconomy platform using Sileo.
 */

interface ToastOptions {
  description?: string;
  duration?: number;
  position?: SileoPosition;
  action?: {
    label: string;
    onClick: () => void;
  };
}

export function useToast() {
  const toast = React.useMemo(
    () => ({
      success: (title: string, options?: ToastOptions) =>
        sileo.success({ title, ...options }),

      error: (title: string, options?: ToastOptions) =>
        sileo.error({ title, ...options }),

      info: (title: string, options?: ToastOptions) =>
        sileo.info({ title, ...options }),

      warning: (title: string, options?: ToastOptions) =>
        sileo.warning({ title, ...options }),

      promise: <T>(
        promise: Promise<T> | (() => Promise<T>),
        data: {
          loading: string;
          success: string | ((data: T) => string);
          error: string | ((error: any) => string);
        }
      ) => {
        // Sileo promise options are slightly different in structure
        return sileo.promise(promise, {
          loading: { title: data.loading },
          success: (res: T) => ({
            title:
              typeof data.success === 'function'
                ? data.success(res)
                : data.success
          }),
          error: (err: any) => ({
            title:
              typeof data.error === 'function' ? data.error(err) : data.error
          })
        });
      },

      dismiss: (id: string) => sileo.dismiss(id),

      clear: () => sileo.clear()
    }),
    []
  );

  return { toast };
}

// Global static export for non-component usage
export { sileo as toast };
